import React, { FunctionComponent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import '@justgiving/loading/src/Loading.scss';
import { captureException } from '@sentry/browser';
import gql from 'graphql-tag';

import { PageSection, SupporterDetails, SupporterDetailsPlaceholder } from '..';
import { makePhotoUrl } from '../../utils/makePhotoUrl';
import SupportersListSkeleton from './SupportersListSkeleton';
import { SupportersListQuery } from '../../../types/__generated__';
import LoadMoreButton from '../LoadMoreButton/LoadMoreButton';
import DonateButton from '../DonateButton/DonateButton';
import styles from './SupportersList.scss';
import { NetworkStatus } from 'apollo-client';
import { SIZE_136_136 } from '../../constants/imageTemplateStrings';
import useFullPageQuery from '../../hooks/useFullPageQuery';

export const DOCUMENT = gql`
  query SupportersList(
    $after: String
    $type: PageType!
    $slug: Slug!
    $preview: Preview
  ) {
    page(type: $type, slug: $slug, preview: $preview) {
      id
      donations(first: 5, after: $after) {
        edges {
          node {
            id
            legacyId
            creationDate
            amount {
              value
              currencyCode
            }
            giftAidAmount {
              value
              currencyCode
            }
            displayName
            avatar
            message
          }
        }
        totalCount
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`;

type SupportersListProps = {
  noSupportersMessage: string;
  displayDonateButton?: boolean;
};

const SupportersList: FunctionComponent<SupportersListProps> = ({
  noSupportersMessage,
  displayDonateButton,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });

  const { error, data, fetchMore, refetch, networkStatus } = useFullPageQuery<
    SupportersListQuery
  >(DOCUMENT, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  if (
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch
  ) {
    return <SupportersListSkeleton animated displayRefreshButton={false} />;
  }

  if (error) {
    captureException(error);
    return <SupportersListSkeleton onRefresh={refetch} />;
  }

  if (!data?.page?.donations) {
    return null;
  }

  const {
    page: {
      donations: {
        edges,
        pageInfo: { hasNextPage, endCursor },
        totalCount,
      },
    },
  } = data;

  async function handleFetchMore() {
    await fetchMore({
      variables: { after: endCursor },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (
          !previousResult?.page?.donations ||
          !fetchMoreResult?.page?.donations
        ) {
          return previousResult;
        }

        return {
          page: {
            ...previousResult.page,
            donations: {
              ...fetchMoreResult.page.donations,
              edges: [
                ...previousResult.page.donations.edges,
                ...fetchMoreResult.page.donations.edges,
              ],
            },
          },
        };
      },
    });
  }

  return (
    <PageSection
      title={
        totalCount === 0
          ? t('supporters.noSupporters', 'No supporters yet')
          : t('supporters.title', 'Supporters')
      }
      hasBorder
      count={totalCount}
      className={styles.container}
    >
      <ul data-qa="supporters-list">
        {totalCount > 0 &&
          edges.map(supporter => {
            const {
              avatar,
              amount,
              giftAidAmount,
              message,
              creationDate,
              displayName,
              legacyId,
            } = supporter.node;
            return (
              <SupporterDetails
                key={legacyId}
                image={makePhotoUrl(avatar, SIZE_136_136)}
                message={message}
                currencyCode={
                  amount && amount.currencyCode ? amount.currencyCode : 'GBP'
                }
                donationAmount={amount && amount.value ? amount.value / 100 : 0}
                giftAidAmount={
                  giftAidAmount && giftAidAmount.value
                    ? giftAidAmount.value / 100
                    : 0
                }
                date={creationDate}
                userName={displayName}
              />
            );
          })}

        {totalCount === 0 && (
          <>
            <p className="jg-space-mbn" data-qa="no-supporters">
              {noSupportersMessage}
            </p>
            <SupporterDetailsPlaceholder />
          </>
        )}
      </ul>

      {hasNextPage && (
        <LoadMoreButton
          className={styles.button}
          loading={networkStatus === NetworkStatus.fetchMore}
          onClick={handleFetchMore}
          trackingData={{
            action_name: 'click',
            subtype: 'button',
            event: 'click',
            event_value: 'show more',
            sub_section: 'supporters',
          }}
        >
          <Trans i18nKey="supporters.showMore">
            Show more
            <span className="jg-sr-only"> supporters</span>
          </Trans>
        </LoadMoreButton>
      )}

      {displayDonateButton && (
        <DonateButton
          className={styles.button}
          inactivePageBehaviour="hide"
          trackingDataPageSection="SupportersList"
        />
      )}
    </PageSection>
  );
};

export default SupportersList;
